import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X, FacebookLogo } from "@phosphor-icons/react";
import { useContent } from "../context/ContentContext";
import { resolveImg } from "../lib/api";

export const Navbar = () => {
  const { content, settings } = useContent();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/membership", label: "Membership" },
    { to: "/contact", label: "Contact" },
    { href: "https://tabletennis.org.nz/tournaments/ttnz-ranking-system-regulations/ttnz-ranking-and-merit-lists/ttnz-ranking-and-merit-lists", label: "Rankings & Competitions", external: true },
  ];
  if (settings.events_enabled) links.push({ to: "/events", label: "Events" });
  if (settings.news_enabled) links.push({ to: "/news", label: "News" });

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 border-b ${
        scrolled ? "bg-white/95 backdrop-blur-md border-navy/10" : "bg-white/90 backdrop-blur-sm border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          {content.logo_image ? (
            <img src={resolveImg(content.logo_image)} alt="TMTT logo" className="h-16 w-16 object-contain" />
          ) : (
            <div className="h-16 w-16 bg-navy text-gold grid place-items-center font-heading font-semibold rounded-sm">TM</div>
          )}
          <div className="leading-none">
            <div className="font-heading font-semibold text-navy text-lg tracking-tight">Te Manawa</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-navy/60">Table Tennis</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            l.external ? (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-sm font-medium tracking-wide py-1 text-navy/70 transition-colors hover:text-gold"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-sm font-medium tracking-wide relative py-1 transition-colors hover:text-gold ${
                  pathname === l.to ? "text-navy" : "text-navy/70"
                }`}
              >
                {l.label}
                {pathname === l.to && <span className="absolute -bottom-0.5 left-0 h-0.5 w-full bg-gold" />}
              </Link>
            )
          ))}
          {settings.facebook_enabled && content.facebook_url && (
            <a
              href={content.facebook_url}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-facebook-link"
              aria-label="Facebook"
              className="text-navy/70 hover:text-gold transition-colors"
            >
              <FacebookLogo size={22} weight="fill" />
            </a>
          )}
          <Link
            to="/membership"
            data-testid="nav-join-btn"
            className="bg-gold text-navy font-semibold text-sm px-6 py-2.5 rounded-sm transition-transform hover:-translate-y-0.5 hover:bg-gold-dark"
          >
            Join Us
          </Link>
        </div>

        <button
          className="lg:hidden text-navy"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={28} /> : <List size={28} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-navy/10 px-5 py-6 space-y-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            l.external ? (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                className="block text-navy font-medium text-lg"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                className="block text-navy font-medium text-lg"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            )
          ))}
          <Link to="/membership" className="block bg-gold text-navy font-semibold text-center py-3 rounded-sm mt-4">
            Join Us
          </Link>
        </div>
      )}
    </header>
  );
};
